import type { Machine } from '../../gql/generated';

/** EDMS categories that carry sorting counts, newest feed first. */
const SORTING_CATEGORIES = ['xcd-machinestats', 'machine-stats'];

export const HEALTH_CATEGORY = 'machine-health';

/** The dimensions the Sorting and Health tabs group EDMS counts by. */
export const STREAM_DIMENSION = 'StreamName';
export const CRITERION_DIMENSION = 'CriterionName';
export const EJECTOR_DIMENSION = 'EjectorIndex';

interface SignalSource {
  category: string;
  signal: string;
}

function findSignal(
  machine: Machine,
  categories: string[],
  signal: string,
  dimension: string
): SignalSource | null {
  // The feed that reported last wins: a machine moves from the older
  // machine-stats feed to xcd-machinestats, and the old one stops.
  const candidates = (machine.availableSignals?.signals ?? [])
    .filter((entry) => categories.includes(entry.category))
    .flatMap((entry) =>
      (entry.signals ?? [])
        .filter((s) => s.signal === signal && s.dimensions?.includes(dimension))
        .map((s) => ({ category: entry.category, lastTimeUtc: s.lastTimeUtc }))
    )
    .sort(
      (a, b) =>
        new Date(b.lastTimeUtc ?? 0).getTime() -
        new Date(a.lastTimeUtc ?? 0).getTime()
    );
  return candidates[0] ? { category: candidates[0].category, signal } : null;
}

/**
 * Where the machine's stream and criteria counts come from, or null when it
 * reports none. Only EDMS builds that group by dimension list the dimensions.
 */
export function sortingSource(machine: Machine) {
  const streams = findSignal(
    machine,
    SORTING_CATEGORIES,
    'stream_counts',
    STREAM_DIMENSION
  );
  if (!streams) return null;
  return {
    streams,
    criteria: findSignal(
      machine,
      [streams.category],
      'criteria_counts',
      CRITERION_DIMENSION
    ),
  };
}

/** Where the machine's per-valve ejector counts come from, if anywhere. */
export function ejectorSource(machine: Machine): SignalSource | null {
  return findSignal(
    machine,
    SORTING_CATEGORIES,
    'ejector_counts',
    EJECTOR_DIMENSION
  );
}

/** The machine's health signals, in the order EDMS lists them. */
export function healthSignals(machine: Machine) {
  return (
    machine.availableSignals?.signals?.find(
      (entry) => entry.category === HEALTH_CATEGORY
    )?.signals ?? []
  );
}
